import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { signOut, supabase } from '../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { getLessons, createLesson, getAIMatchedInstructors } from '../../lib/supabase'
import { Plus, Star, Clock, Check, Zap, LogOut } from 'lucide-react'
import { format } from 'date-fns'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)

function PaymentForm({ price, authorizeOnly, onSuccess, onError }) {
  const stripe = useStripe()
  const elements = useElements()
  const [paying, setPaying] = useState(false)

  const handlePay = async (e) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setPaying(true)
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {},
      redirect: 'if_required',
    })
    if (error) {
      onError(error.message)
      setPaying(false)
    } else {
      onSuccess()
    }
  }

  return (
    <form onSubmit={handlePay}>
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        disabled={paying || !stripe}
        style={{
          marginTop: '1.25rem', width: '100%',
          background: paying ? 'rgba(11,30,61,0.4)' : 'var(--navy)',
          color: 'white', border: 'none', borderRadius: 10,
          padding: '14px', fontSize: '0.95rem', fontWeight: 700,
          cursor: paying ? 'not-allowed' : 'pointer',
          fontFamily: 'DM Sans, sans-serif',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {paying
          ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Processing...</>
          : authorizeOnly ? `Authorize Card — $${price}` : `Pay $${price}`
        }
      </button>
    </form>
  )
}

function BookingWizard({ onClose, onBooked, schoolLessonTypes = [], schoolId, chargesEnabled = false, paymentMode = 'upfront' }) {
  const { user } = useAuth()
  const [step, setStep] = useState(1)
  const [form, setForm] = useState({
    lesson_type: '', location: '', scheduled_date: '',
    start_time: '09:00', end_time: '10:00', num_students: 1, skill_level: 'beginner', notes: ''
  })
  const [matches, setMatches] = useState([])
  const [selectedInstructor, setSelectedInstructor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [booked, setBooked] = useState(false)
  const [clientSecret, setClientSecret] = useState(null)
  const [pendingPaymentIntentId, setPendingPaymentIntentId] = useState(null)
  const [paymentError, setPaymentError] = useState('')
  const [processingPayment, setProcessingPayment] = useState(false)
  const isAuthorizeMode = paymentMode === 'authorize'

  const getPrice = (lessonType) => {
    const found = schoolLessonTypes.find(t => t.lesson_type === lessonType)
    return found ? found.price : 75
  }

  const getLabel = (lessonType) => {
    const found = schoolLessonTypes.find(t => t.lesson_type === lessonType)
    return found ? (found.label || lessonType) : lessonType
  }

  const set = k => v => setForm(f => ({ ...f, [k]: v }))

  const handleProceedToPayment = async () => {
    if (!chargesEnabled) {
      await confirmBooking()
      return
    }
    setProcessingPayment(true)
    setPaymentError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/bookings/charge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ lesson_type: form.lesson_type }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Payment setup failed')
      setClientSecret(data.clientSecret)
      setPendingPaymentIntentId(data.paymentIntentId)
      setStep(5)
    } catch (err) {
      setPaymentError(err.message)
    }
    setProcessingPayment(false)
  }

  const findMatches = async () => {
    setLoading(true)
    const results = await getAIMatchedInstructors(form)
    setMatches(results.slice(0, 4))
    setLoading(false)
    setStep(3)
  }

  const confirmBooking = async (paymentIntentId = null) => {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/bookings/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          ...form,
          instructor_id: selectedInstructor?.id || null,
          ai_matched: !!selectedInstructor,
          ai_match_score: selectedInstructor?.match_score / 100 || null,
          ...(paymentIntentId ? { payment_intent_id: paymentIntentId, payment_captured: false } : {}),
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to save your booking')
      setBooked(true)
    } catch (err) {
      setPaymentError(err.message)
    }
    setLoading(false)
  }

  const getAvailableTimes = () => {
    const all = ['07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00']
    const selected = schoolLessonTypes.find(t => t.lesson_type === form.lesson_type)
    if (!selected || !selected.hours_from || !selected.hours_to) return all
    return all.filter(t => t >= selected.hours_from && t < selected.hours_to)
  }

  if (booked) return (
    <div className="modal-overlay">
      <div className="modal" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(14,170,164,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: 28 }}>✅</div>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', marginBottom: '0.75rem' }}>Booking Confirmed!</h2>
        <p style={{ color: 'var(--text-mid)', marginBottom: '0.5rem' }}>
          Your {getLabel(form.lesson_type)} lesson
        </p>
        <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginBottom: '2rem' }}>
          {form.scheduled_date} · {form.start_time}–{form.end_time}
        </p>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginBottom: '1.5rem' }}>
          {selectedInstructor ? `Your instructor is ${selectedInstructor.profile?.full_name}.` : 'Your school will assign an instructor and confirm shortly.'}
        </p>
        <button className="btn btn-primary" onClick={() => { onBooked(); onClose() }}>
          View My Lessons
        </button>
      </div>
    </div>
  )

  const steps = [
    { n: 1, label: 'Choose lesson' },
    { n: 2, label: 'Pick date' },
    { n: 3, label: 'Instructor' },
    { n: 4, label: 'Confirm' },
    ...(chargesEnabled ? [{ n: 5, label: 'Payment' }] : []),
  ]

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }}>
        {/* Step indicator */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(11,30,61,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {steps.map((s, i) => (
              <div key={s.n} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '0.75rem',
                    background: step > s.n ? 'var(--teal)' : step === s.n ? 'var(--coral)' : 'rgba(11,30,61,0.08)',
                    color: step >= s.n ? 'white' : 'var(--text-light)',
                    transition: 'all 0.3s'
                  }}>
                    {step > s.n ? <Check size={14} /> : s.n}
                  </div>
                  <div style={{ fontSize: '0.62rem', color: step === s.n ? 'var(--text-dark)' : 'var(--text-light)', marginTop: 3, fontWeight: step === s.n ? 600 : 400, whiteSpace: 'nowrap' }}>{s.label}</div>
                </div>
                {i < steps.length - 1 && (
                  <div style={{ flex: 1, height: 1.5, background: step > s.n ? 'var(--teal)' : 'rgba(11,30,61,0.1)', transition: 'background 0.3s', marginTop: -14 }} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {/* Step 1: Lesson type */}
          {step === 1 && (
            <div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', marginBottom: '0.375rem' }}>What type of lesson?</h3>
              <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>Choose the lesson that matches your experience level.</p>
              {schoolLessonTypes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)', background: 'rgba(11,30,61,0.03)', borderRadius: 10, fontSize: '0.875rem' }}>
                  No lesson types available yet. Contact your school to get set up.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {schoolLessonTypes.filter(t => t.active !== false).map(t => (
                    <button key={t.lesson_type} onClick={() => { set('lesson_type')(t.lesson_type); set('skill_level')('beginner') }}
                      style={{
                        padding: '0.875rem', borderRadius: 10, border: '1.5px solid',
                        borderColor: form.lesson_type === t.lesson_type ? 'var(--coral)' : 'rgba(11,30,61,0.12)',
                        background: form.lesson_type === t.lesson_type ? 'rgba(232,97,74,0.06)' : 'white',
                        cursor: 'pointer', textAlign: 'left', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s'
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>{t.label || t.lesson_type}</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--teal)', flexShrink: 0 }}>${t.price}</div>
                      </div>
                      {t.description && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{t.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Date & time */}
          {step === 2 && (
            <div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', marginBottom: '0.375rem' }}>When would you like to surf?</h3>
              <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>Pick your preferred date and time.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  {(() => {
                    const selected = schoolLessonTypes.find(t => t.lesson_type === form.lesson_type)
                    const today = format(new Date(), 'yyyy-MM-dd')
                    const minDate = selected?.available_from && selected.available_from > today ? selected.available_from : today
                    const maxDate = selected?.available_to || undefined
                    return (
                      <input className="form-control" type="date"
                        min={minDate} max={maxDate}
                        value={form.scheduled_date}
                        onChange={e => set('scheduled_date')(e.target.value)} required />
                    )
                  })()}
                  {(() => {
                    const selected = schoolLessonTypes.find(t => t.lesson_type === form.lesson_type)
                    if (selected?.available_from && selected?.available_to) {
                      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
                      const fmt = (d) => { const [,m,day] = d.split('-'); return `${months[parseInt(m)-1]} ${parseInt(day)}` }
                      return <div style={{ fontSize: '0.75rem', color: 'var(--teal)', marginTop: 4 }}>📅 Available {fmt(selected.available_from)} – {fmt(selected.available_to)}</div>
                    }
                    return null
                  })()}
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Start Time</label>
                    <select className="form-control" value={form.start_time} onChange={e => { set('start_time')(e.target.value); const [h,m] = e.target.value.split(':').map(Number); set('end_time')(`${String(h+1).padStart(2,'0')}:${String(m).padStart(2,'0')}`) }}>
                      {getAvailableTimes().map(t => <option key={t} value={t}>{t.replace(':','').length===4 && t[0]==='0' ? t.slice(1) : t} {parseInt(t)<12?'AM':'PM'}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Duration</label>
                    <select className="form-control" onChange={e => {
                      const [h,m] = form.start_time.split(':').map(Number)
                      const dur = parseInt(e.target.value)
                      const eh = Math.floor((h * 60 + m + dur) / 60), em = (h * 60 + m + dur) % 60
                      set('end_time')(`${String(eh).padStart(2,'0')}:${String(em).padStart(2,'0')}`)
                    }}>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Number of Students</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[1,2,3,4].map(n => (
                      <button key={n} onClick={() => set('num_students')(n)} style={{
                        width: 44, height: 44, borderRadius: 8, border: '1.5px solid',
                        borderColor: form.num_students === n ? 'var(--coral)' : 'rgba(11,30,61,0.12)',
                        background: form.num_students === n ? 'rgba(232,97,74,0.08)' : 'white',
                        fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                        color: form.num_students === n ? 'var(--coral)' : 'var(--text-mid)'
                      }}>{n}</button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes (optional)</label>
                  <textarea className="form-control" rows={2} placeholder="Any special requests or info..." value={form.notes} onChange={e => set('notes')(e.target.value)} style={{ resize: 'none' }} />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Request instructor (optional) */}
          {step === 3 && (
            <div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', marginBottom: '0.375rem' }}>
                Request an instructor
              </h3>
              <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
                Pick a preferred instructor or skip — your school will assign one for you.
              </p>

              {/* Skip / no preference option */}
              <button
                onClick={() => setSelectedInstructor(null)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '0.875rem 1rem', borderRadius: 10, border: '1.5px solid',
                  borderColor: selectedInstructor === null ? 'var(--coral)' : 'rgba(11,30,61,0.1)',
                  background: selectedInstructor === null ? 'rgba(232,97,74,0.06)' : 'rgba(11,30,61,0.02)',
                  cursor: 'pointer', marginBottom: 10, textAlign: 'left', transition: 'all 0.2s',
                  fontFamily: 'DM Sans, sans-serif'
                }}
              >
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(11,30,61,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                  🤙
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dark)' }}>No preference</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: 2 }}>
                    Your school will assign the best available instructor
                  </div>
                </div>
                {selectedInstructor === null && (
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--coral)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Check size={14} color="white" />
                  </div>
                )}
              </button>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0.75rem 0' }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(11,30,61,0.08)' }} />
                <span style={{ fontSize: '0.72rem', color: 'var(--text-light)' }}>or choose one</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(11,30,61,0.08)' }} />
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  <div className="spinner" style={{ margin: '0 auto 1rem' }} />
                  Loading instructors...
                </div>
              ) : matches.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1.5rem', background: 'rgba(11,30,61,0.03)', borderRadius: 10, color: 'var(--text-light)', fontSize: '0.875rem' }}>
                  No available instructors found for this time slot.
                </div>
              ) : matches.map(inst => (
                <button key={inst.id} onClick={() => setSelectedInstructor(inst)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '0.875rem 1rem', borderRadius: 10, border: '1.5px solid',
                  borderColor: selectedInstructor?.id === inst.id ? 'var(--teal)' : 'rgba(11,30,61,0.1)',
                  background: selectedInstructor?.id === inst.id ? 'rgba(14,170,164,0.06)' : 'white',
                  cursor: 'pointer', marginBottom: 8, textAlign: 'left', transition: 'all 0.2s',
                  fontFamily: 'DM Sans, sans-serif'
                }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--ocean)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                    {(inst.profile?.full_name || 'U').split(' ').map(n=>n[0]).join('').slice(0,2)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-dark)' }}>{inst.profile?.full_name}</div>
                      <div style={{
                        fontSize: '0.8rem', fontWeight: 700, padding: '2px 10px', borderRadius: 50,
                        background: inst.match_score > 80 ? 'rgba(14,170,164,0.1)' : 'rgba(212,168,75,0.1)',
                        color: inst.match_score > 80 ? 'var(--teal)' : 'var(--gold)'
                      }}>
                        {inst.match_score}% match
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, fontSize: '0.75rem', color: 'var(--text-light)', marginTop: 2 }}>
                      <span><Star size={11} fill="var(--gold)" color="var(--gold)" style={{ display: 'inline' }} /> {inst.rating?.toFixed(1)}</span>
                      <span style={{ textTransform: 'capitalize' }}>{inst.specialties?.slice(0,2).join(', ')}</span>
                    </div>
                  </div>
                  {selectedInstructor?.id === inst.id && (
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Check size={14} color="white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 4 && (
            <div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', marginBottom: '1.25rem' }}>Confirm your booking</h3>
              <div style={{ background: 'rgba(11,30,61,0.03)', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem' }}>
                {[
                  { label: 'Lesson', value: form.lesson_type },
                  { label: 'Date', value: form.scheduled_date },
                  { label: 'Time', value: `${form.start_time} – ${form.end_time}` },
                  { label: 'Students', value: form.num_students },
                  { label: 'Instructor', value: selectedInstructor ? selectedInstructor.profile?.full_name : 'Assigned by school' },
                  { label: 'Price', value: `$${getPrice(form.lesson_type)}` },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid rgba(11,30,61,0.05)', fontSize: '0.875rem' }}>
                    <span style={{ color: 'var(--text-light)', textTransform: 'capitalize' }}>{r.label}</span>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{r.value}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding: '0.875rem', background: 'rgba(14,170,164,0.06)', border: '1px solid rgba(14,170,164,0.2)', borderRadius: 8, fontSize: '0.8rem', color: 'var(--text-mid)' }}>
                {selectedInstructor
                  ? `✅ You requested ${selectedInstructor.profile?.full_name}. Your school will confirm shortly.`
                  : '🤙 Your school will assign the best available instructor and confirm your lesson.'
                }
              </div>
              {paymentError && (
                <div style={{ marginTop: 12, padding: '0.75rem 1rem', borderRadius: 8, background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.25)', color: 'var(--coral)', fontSize: '0.825rem' }}>
                  {paymentError}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Payment */}
          {step === 5 && clientSecret && (
            <div>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', marginBottom: '0.375rem' }}>
                {isAuthorizeMode ? 'Authorize Card' : 'Complete Payment'}
              </h3>
              <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>
                {isAuthorizeMode
                  ? 'Your card will be authorized but not charged. The school will charge you on the day of your lesson.'
                  : 'Your lesson is held for 10 minutes. Enter your card details to confirm.'
                }
              </p>
              <div style={{ background: 'rgba(14,170,164,0.05)', border: '1px solid rgba(14,170,164,0.15)', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--text-light)' }}>{isAuthorizeMode ? 'Amount to authorize: ' : 'Total due: '}</span>
                <span style={{ fontWeight: 800, color: 'var(--teal)', fontSize: '1rem' }}>${getPrice(form.lesson_type)}</span>
              </div>
              {paymentError && (
                <div style={{ marginBottom: 12, padding: '0.75rem 1rem', borderRadius: 8, background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.25)', color: 'var(--coral)', fontSize: '0.825rem' }}>
                  {paymentError}
                </div>
              )}
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <PaymentForm
                  price={getPrice(form.lesson_type)}
                  authorizeOnly={isAuthorizeMode}
                  onSuccess={async () => { await confirmBooking(pendingPaymentIntentId) }}
                  onError={(msg) => setPaymentError(msg)}
                />
              </Elements>
            </div>
          )}
        </div>

        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(11,30,61,0.08)', display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
          {step > 1 ? (
            <button className="btn btn-ghost" onClick={() => setStep(s => s - 1)}>← Back</button>
          ) : (
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          )}
          {step === 1 && (
            <button className="btn btn-primary" disabled={!form.lesson_type} onClick={() => setStep(2)}>
              Next →
            </button>
          )}
          {step === 2 && (
            <button className="btn btn-primary" disabled={!form.scheduled_date} onClick={findMatches}>
              ⚡ Find Instructors →
            </button>
          )}
          {step === 3 && (
            <button className="btn btn-primary" onClick={() => setStep(4)}>
              Continue →
            </button>
          )}
          {step === 4 && (
            <button className="btn btn-primary" onClick={handleProceedToPayment} disabled={processingPayment || loading}>
              {processingPayment ? 'Loading...' : !chargesEnabled ? '✓ Confirm Booking' : isAuthorizeMode ? `Authorize Card →` : `Pay $${getPrice(form.lesson_type)} →`}
            </button>
          )}
          {step === 5 && null}
        </div>
      </div>
    </div>
  )
}

export default function CustomerApp() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [lessons, setLessons] = useState([])
  const [loading, setLoading] = useState(true)
  const [showBook, setShowBook] = useState(false)
  const [schoolLessonTypes, setSchoolLessonTypes] = useState([])
  const [schoolChargesEnabled, setSchoolChargesEnabled] = useState(false)
  const [schoolPaymentMode, setSchoolPaymentMode] = useState('upfront')
  const [schoolId, setSchoolId] = useState(null)

  const loadLessons = () => {
    if (!user) return
    getLessons({ customer_id: user.id }).then(({ data }) => {
      setLessons(data || [])
      setLoading(false)
    })
  }

  const loadPricing = async () => {
    if (!user?.id) return
    // Get the session token to pass to the server-side endpoint
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) return

    const res = await fetch('/api/school?action=my-pricing', {
      headers: { Authorization: `Bearer ${session.access_token}` }
    })
    if (!res.ok) return
    const data = await res.json()
    setSchoolLessonTypes(data.pricing || [])
    setSchoolChargesEnabled(data.school?.charges_enabled || false)
    setSchoolPaymentMode(data.school?.payment_mode || 'upfront')
    if (data.schoolId) setSchoolId(data.schoolId)
  }

  useEffect(() => {
    if (!user) return
    loadLessons()
    loadPricing()

    const channel = supabase
      .channel(`customer-lessons-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'lessons',
        filter: `customer_id=eq.${user.id}`,
      }, () => { loadLessons() })
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [user])

  const handleSignOut = async () => { await signOut(); navigate('/') }

  const upcoming = lessons.filter(l => l.scheduled_date >= format(new Date(), 'yyyy-MM-dd') && l.status !== 'cancelled')
  const past = lessons.filter(l => l.scheduled_date < format(new Date(), 'yyyy-MM-dd') || l.status === 'completed')

  const STATUS = {
    pending: { label: 'Pending', cls: 'badge-gold' },
    confirmed: { label: 'Confirmed', cls: 'badge-teal' },
    completed: { label: 'Completed', cls: 'badge-green' },
    cancelled: { label: 'Cancelled', cls: 'badge-coral' },
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F0EDE4' }}>
      {/* Header */}
      <div style={{ background: 'var(--navy)', paddingTop: 'env(safe-area-inset-top)' }}>
        <div style={{ padding: '0 2rem' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', fontWeight: 900, color: 'white' }}>
            <div style={{ width: 30, height: 30, background: 'var(--coral)', borderRadius: '50% 50% 50% 0', display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'rotate(-45deg)' }}>
              <span style={{ transform: 'rotate(45deg)', display: 'block', fontSize: 14 }}>🌊</span>
            </div>
            WaveSync
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)' }}>
              Hey, {profile?.full_name?.split(' ')[0] || 'surfer'} 🤙
            </span>
            <button className="btn btn-ghost btn-sm" onClick={handleSignOut} style={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.15)' }}>
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Hero CTA */}
      <div style={{ background: 'var(--navy)', paddingBottom: '3rem' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '2.5rem 2rem 0', textAlign: 'center' }}>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2.5rem', fontWeight: 900, color: 'white', marginBottom: '0.75rem' }}>
            Book Your Next Session
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', marginBottom: '1.75rem' }}>
            Tell us what you need and we'll take care of the rest.
          </p>
          <button className="btn btn-primary btn-lg" onClick={() => setShowBook(true)}>
            <Plus size={18} /> Book a Lesson
          </button>
        </div>
      </div>

      {/* Lessons */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '2rem' }}>
        {upcoming.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem' }}>Upcoming Lessons</h2>
            {upcoming.map(l => {
              const s = STATUS[l.status] || STATUS.pending
              return (
                <div key={l.id} className="card" style={{ marginBottom: '0.75rem', padding: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{
                      width: 50, minWidth: 50, background: 'rgba(14,170,164,0.08)', borderRadius: 10,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.5rem',
                      border: '1px solid rgba(14,170,164,0.15)'
                    }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {format(new Date(l.scheduled_date + 'T00:00:00'), 'MMM')}
                      </div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-dark)', lineHeight: 1 }}>
                        {format(new Date(l.scheduled_date + 'T00:00:00'), 'd')}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 6 }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, textTransform: 'capitalize' }}>
                          {l.lesson_type} Lesson
                          {l.ai_matched && <span style={{ marginLeft: 6, fontSize: '0.65rem', color: 'var(--teal)' }}>⚡ AI Matched</span>}
                        </h3>
                        <span className={`badge ${s.cls}`}>{s.label}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 4, flexWrap: 'wrap' }}>
                        <span><Clock size={11} style={{ display: 'inline', marginRight: 3 }} />{l.start_time?.slice(0,5)} – {l.end_time?.slice(0,5)}</span>
                        {l.instructor && <span>👤 {l.instructor.profile?.full_name}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {!loading && lessons.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div style={{ fontSize: 48, marginBottom: '1rem' }}>🏄</div>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', marginBottom: '0.5rem' }}>No lessons yet</h3>
            <p style={{ color: 'var(--text-light)', marginBottom: '1.5rem' }}>Book your first surf lesson and get matched with a top instructor.</p>
            <button className="btn btn-primary" onClick={() => setShowBook(true)}><Plus size={16} /> Book First Lesson</button>
          </div>
        )}

        {past.length > 0 && (
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-mid)' }}>Past Lessons</h2>
            {past.slice(0,5).map(l => (
              <div key={l.id} style={{
                background: 'white', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '0.5rem',
                opacity: 0.7, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                border: '1px solid rgba(11,30,61,0.07)'
              }}>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, textTransform: 'capitalize' }}>{l.lesson_type} Lesson</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{l.scheduled_date}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {l.instructor && <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{l.instructor.profile?.full_name}</div>}
                  <span className={`badge ${STATUS[l.status]?.cls || 'badge-gray'}`}>{STATUS[l.status]?.label || l.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showBook && <BookingWizard onClose={() => setShowBook(false)} onBooked={loadLessons} schoolLessonTypes={schoolLessonTypes} schoolId={schoolId} chargesEnabled={schoolChargesEnabled} paymentMode={schoolPaymentMode} />}
    </div>
  )
}
